// GitHub Actions deploy dispatch modal. The operator picks a staging
// environment; the shipping-service pins the workflow per env and dispatches
// it on the ticket's own PR branch (both derived server-side). Environments
// flagged requiresApproval require typing confirm="deploy".

import { useEffect, useMemo, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, Spinner, apiErrorMessage } from "./shared";

export default function DeployModal({
  open,
  onClose,
  ticketKey,
  branch,
  environments,
  repoId,
  onDeployed
}) {
  const workflows = useMemo(() => environments || [], [environments]);

  // The staging axis is always present; dedupe the workflow list down to its
  // distinct staging ids. For multi-app repos (e.g. boompay-client) the same
  // staging id repeats once per app, so first-seen wins for the label/desc.
  const stagings = useMemo(() => {
    const seen = new Map();
    workflows.forEach((w) => {
      if (!seen.has(w.id)) seen.set(w.id, w);
    });
    return Array.from(seen.values());
  }, [workflows]);

  // The app axis only exists for monorepos: every workflow carries `app`.
  const multiApp = useMemo(
    () => workflows.some((w) => w.app),
    [workflows]
  );
  const apps = useMemo(() => {
    if (!multiApp) return [];
    const seen = new Map();
    workflows.forEach((w) => {
      if (w.app && !seen.has(w.app)) {
        seen.set(w.app, { app: w.app, appLabel: w.appLabel || w.app });
      }
    });
    return Array.from(seen.values());
  }, [workflows, multiApp]);

  const [envId, setEnvId] = useState("");
  const [appId, setAppId] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setEnvId(stagings.length ? stagings[0].id : "");
    setAppId(apps.length ? apps[0].app : "");
    setConfirm("");
    setSubmitting(false);
    setSubmitError(null);
  }, [open, stagings, apps]);

  const selected = stagings.find((e) => e.id === envId);
  const selectedApp = apps.find((a) => a.app === appId);
  // The server resolves the concrete workflow file from (env, app); the modal
  // only flags confirmation off the staging-axis option (requiresApproval is
  // identical across a staging id's per-app duplicates).
  const needsConfirm = selected && selected.requiresApproval;
  const canSubmit =
    !!selected &&
    !!branch &&
    (!multiApp || !!selectedApp) &&
    (!needsConfirm || confirm === "deploy") &&
    !submitting;

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    // Only the env is sent — the service pins the workflow and derives the
    // ticket's PR branch itself.
    const body = { key: ticketKey, env: selected.id };
    if (multiApp) body.app = selectedApp.app;
    if (repoId) body.repoId = repoId;
    if (needsConfirm) body.confirm = "deploy";
    api
      .bssDeployTicket(body)
      .then((res) => {
        setSubmitting(false);
        onDeployed && onDeployed(res.data, selected);
        onClose && onClose();
      })
      .catch((err) => {
        setSubmitting(false);
        setSubmitError(apiErrorMessage(err, "Could not dispatch the workflow."));
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Trigger GitHub Action"
      width={620}
      footer={
        <>
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                <Spinner /> Dispatching…
              </>
            ) : (
              <>
                <Icon.PlayCircle size={13} /> Run{" "}
                {multiApp && selectedApp ? selectedApp.appLabel : "workflow"} on{" "}
                {selected ? selected.label : "…"}
              </>
            )}
          </button>
        </>
      }
    >
      <div className="bss-ga-modal">
        <div className="bss-ga-meta">
          <div className="bss-ga-meta-row">
            <div className="bss-ga-meta-k">Ticket</div>
            <div className="bss-ga-meta-v">
              <Icon.Ticket
                size={12}
                style={{ color: "var(--bss-boom-pink)" }}
              />
              <span
                style={{
                  fontFamily: "var(--bss-font-mono)",
                  fontWeight: 500
                }}
              >
                {ticketKey}
              </span>
            </div>
          </div>
          {repoId && (
            <div className="bss-ga-meta-row">
              <div className="bss-ga-meta-k">Repo</div>
              <div className="bss-ga-meta-v">
                <Icon.GitBranch size={12} />
                <code className="bss-code-inline">{repoId}</code>
              </div>
            </div>
          )}
          <div className="bss-ga-meta-row">
            <div className="bss-ga-meta-k">Branch</div>
            <div className="bss-ga-meta-v">
              <Icon.GitBranch size={12} />
              {branch ? (
                <code className="bss-code-inline">{branch}</code>
              ) : (
                <span
                  style={{
                    color: "var(--bss-fg-subtle)",
                    fontStyle: "italic",
                    fontSize: 12
                  }}
                >
                  No PR branch yet — deploy unavailable until the ticket has
                  one.
                </span>
              )}
            </div>
          </div>
        </div>

        {multiApp && (
          <div className="bss-ga-section">
            <div className="bss-ga-section-hd">
              <span className="bss-ga-section-title">App</span>
              <span className="bss-ga-section-sub">
                This repo ships multiple apps — pick which one to deploy.
              </span>
            </div>
            <div className="bss-ga-env-grid">
              {apps.map((a) => {
                const active = appId === a.app;
                return (
                  <button
                    key={a.app}
                    type="button"
                    className={`bss-ga-env ${active ? "is-active" : ""}`.trim()}
                    onClick={() => setAppId(a.app)}
                  >
                    <div className="bss-ga-env-radio">
                      <span className="bss-ga-env-radio-dot" />
                    </div>
                    <div className="bss-ga-env-body">
                      <div className="bss-ga-env-name">{a.appLabel}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bss-ga-section">
          <div className="bss-ga-section-hd">
            <span className="bss-ga-section-title">Environment</span>
            <span className="bss-ga-section-sub">
              Which staging stack should this branch deploy to?
            </span>
          </div>
          <div className="bss-ga-env-grid">
            {stagings.map((e) => {
              const active = envId === e.id;
              return (
                <button
                  key={e.id}
                  type="button"
                  className={`bss-ga-env ${active ? "is-active" : ""} ${
                    e.kind === "production" ? "is-prod" : ""
                  }`.trim()}
                  onClick={() => setEnvId(e.id)}
                >
                  <div className="bss-ga-env-radio">
                    <span className="bss-ga-env-radio-dot" />
                  </div>
                  <div className="bss-ga-env-body">
                    <div className="bss-ga-env-name">
                      {e.label}
                      {e.kind === "production" && (
                        <span className="bss-ga-env-pill">live</span>
                      )}
                      {e.kind === "sandbox" && (
                        <span className="bss-ga-env-pill bss-ga-env-pill--sandbox">
                          external
                        </span>
                      )}
                    </div>
                    <div className="bss-ga-env-desc">{e.desc}</div>
                  </div>
                </button>
              );
            })}
            {stagings.length === 0 && (
              <div
                style={{
                  color: "var(--bss-fg-muted)",
                  fontSize: 12.5,
                  gridColumn: "1 / -1"
                }}
              >
                No deploy environments are configured.
              </div>
            )}
          </div>
        </div>

        {needsConfirm && (
          <div className="bss-ga-warn">
            <Icon.AlertTriangle
              size={14}
              style={{
                color: "var(--bss-danger)",
                flexShrink: 0,
                marginTop: 1
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--bss-fg-strong)",
                  marginBottom: 4
                }}
              >
                This environment requires confirmation.
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--bss-fg-muted)",
                  marginBottom: 10,
                  lineHeight: 1.5
                }}
              >
                Type <code className="bss-code-inline">deploy</code> below to
                confirm.
              </div>
              <input
                className="bss-input"
                placeholder="Type 'deploy' to confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ maxWidth: 280 }}
                aria-label="Deploy confirmation"
              />
            </div>
          </div>
        )}

        {submitError && (
          <div style={{ fontSize: 12.5, color: "var(--bss-danger)" }}>
            {submitError}
          </div>
        )}
      </div>
    </Modal>
  );
}
